import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiLock, FiUser, FiArrowRight, FiCheck, FiAlertCircle, FiChevronDown } from 'react-icons/fi';
import { motion } from 'framer-motion';

function SignupModern({ onLogin }) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    user_type: 'client',
    agree_terms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!formData.agree_terms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/signup.php`,
        {
          full_name: formData.full_name,
          email: formData.email,
          password: formData.password,
          user_type: formData.user_type
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        onLogin(response.data.token, response.data.user);
        navigate('/dashboard');
      } else {
        setError(response.data.message || 'Sign up failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Expert legal guidance from top lawyers',
    'Book consultations in minutes',
    '24/7 support & case tracking',
    'Fully confidential & secure',
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/" className="flex items-center gap-2.5 mb-12">
              <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                <span className="text-lg">⚖️</span>
              </div>
              <span className="text-xl font-bold text-white">Bihar Lawyer</span>
            </Link>

            <h1 className="text-4xl font-bold text-white mb-4 leading-tight">Start Your Legal<br/>Journey Today</h1>
            <p className="text-white/50 text-lg mb-10 max-w-md">
              Join our platform to get started with professional legal services and expert consultations.
            </p>

            <div className="space-y-4">
              {benefits.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0">
                    <FiCheck className="w-3.5 h-3.5 text-accent-400" />
                  </div>
                  <span className="text-white/70 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-secondary-50">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                <span className="text-lg">⚖️</span>
              </div>
              <span className="text-xl font-bold text-secondary-900">Bihar Lawyer</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-card p-8 border border-secondary-100">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">Create Account</h2>
              <p className="text-secondary-500 text-sm">Fill in your details to get started</p>
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-danger-50 border border-danger-500/20 mb-6">
                <FiAlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0" />
                <p className="text-sm text-danger-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Full Name</label>
                <div className="relative">
                  <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="input pl-11"
                  />
                </div>
              </div>

              <div>
                <label className="label">Email Address</label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="input pl-11"
                  />
                </div>
              </div>

              <div>
                <label className="label">Account Type</label>
                <div className="relative">
                  <select
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleChange}
                    className="select-input"
                  >
                    <option value="client">Client</option>
                    <option value="lawyer">Lawyer</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Password</label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      className="input pl-11"
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Confirm</label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                    <input
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      className="input pl-11"
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="agree_terms"
                  checked={formData.agree_terms}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-500 mt-0.5"
                />
                <span className="text-sm text-secondary-500">
                  I agree to the <a href="#" className="text-primary-500 hover:underline">Terms of Service</a> and <a href="#" className="text-primary-500 hover:underline">Privacy Policy</a>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5 text-base gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-secondary-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-secondary-400">or continue with</span>
              </div>
            </div>

            <button type="button" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-secondary-200 text-secondary-700 font-medium hover:bg-secondary-50 transition-colors text-sm">
              <span className="text-lg">🔍</span>
              Google
            </button>

            <p className="text-center text-sm text-secondary-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-primary-500 hover:text-primary-600 transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default SignupModern;
